import { CONTRACT_ACTION_BASES_URL, CONTRACT_URL } from "../../../constants";
import { getToken } from "../../../context/authContext";
import { hasRealmRole, STAFF_ROLES } from "../../../auth/oidc";
import { ApiServiceManager, fetchRequest } from "../../apiServiceManager";
import { ESenderRole } from "../contract/models";
import {
  IContractActionHistoryRequestBody,
  IContractActionHistoryResponse,
  IContractActionRequestBody,
  IContractActionResponse,
} from "./models";

const defaultProvide = async () => await getToken();

/**
 * The actorRole an admin-web staff member should send for a general admin-tier contract
 * action (e.g. ADMIN_RESOLVE_ESCALATION, ADMIN_CONFIRM_DELIVERY). Backend authorization
 * (contract-action-service's UserPermissionEvaluator) is keyed on this matching the
 * caller's real KC-derived authority — sending the wrong one is always denied, regardless
 * of what the user actually holds. Priority mirrors the backend's own tiers: full ADMIN,
 * then ADMIN_OPERATIONS (staff-admin-ops), then ADMIN_SETTLEMENT (staff-admin-settle).
 * Falls back to ADMIN for every other tier (ADMIN_FINANCE/ADMIN_VIEWER/ADMIN_CS-only) —
 * unchanged from previous behavior; those tiers aren't wired up on the backend yet.
 *
 * Do NOT use this for ADMIN_SETTLE_CONTRACT/ADMIN_RESETTLE_CONTRACT — use
 * getSettlementActorRole() instead. Those two are ADMIN_SETTLEMENT-exclusive on the
 * backend (contract-engine's SettleContractValidator only accepts ADMIN/SYSTEM/
 * ADMIN_SETTLEMENT as the settling actor), so a staff-admin-ops+staff-admin-settle user
 * would otherwise get ADMIN_OPERATIONS picked here (ops is checked first) and fail
 * downstream validation despite this evaluator approving the request.
 */
export const getAdminActorRole = (): ESenderRole => {
  if (hasRealmRole(STAFF_ROLES.ADMIN)) return ESenderRole.ADMIN;
  if (hasRealmRole(STAFF_ROLES.ADMIN_OPS)) return ESenderRole.ADMIN_OPERATIONS;
  if (hasRealmRole(STAFF_ROLES.ADMIN_SETTLE))
    return ESenderRole.ADMIN_SETTLEMENT;
  return ESenderRole.ADMIN;
};

/**
 * The actorRole to send specifically for ADMIN_SETTLE_CONTRACT/ADMIN_RESETTLE_CONTRACT
 * (and the rest of the settlement-modal family: draft/redraft/accept-suggested-settlement).
 * Settlement is ADMIN_SETTLEMENT-exclusive end-to-end (contract-action-service's
 * getAdminSettlementActions() + contract-engine's SettleContractValidator), so this
 * checks ADMIN_SETTLE before ADMIN_OPS — the reverse priority from getAdminActorRole() —
 * so a staff-admin-ops+staff-admin-settle user correctly settles as ADMIN_SETTLEMENT
 * instead of getting the ops tier picked and rejected downstream.
 */
export const getSettlementActorRole = (): ESenderRole => {
  if (hasRealmRole(STAFF_ROLES.ADMIN)) return ESenderRole.ADMIN;
  if (hasRealmRole(STAFF_ROLES.ADMIN_SETTLE))
    return ESenderRole.ADMIN_SETTLEMENT;
  if (hasRealmRole(STAFF_ROLES.ADMIN_OPS)) return ESenderRole.ADMIN_OPERATIONS;
  return ESenderRole.ADMIN;
};

export const doAction = (
  body: IContractActionRequestBody
): Promise<IContractActionResponse> => {
  const descriptor = new ApiServiceManager(
    `${CONTRACT_ACTION_BASES_URL}`,
    "POST",
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const externalDoAction = (
  body: IContractActionRequestBody
): Promise<IContractActionResponse> => {
  const descriptor = new ApiServiceManager(
    `${CONTRACT_ACTION_BASES_URL}external`,
    "POST",
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const getActionsHistory = (
  body: IContractActionHistoryRequestBody
): Promise<IContractActionHistoryResponse> => {
  const descriptor = new ApiServiceManager(
    `${CONTRACT_URL}history?${
      body?.contractId ? `contractId=${body?.contractId || ""}&` : ""
    }${
      body?.actorIds?.length ? `actorIds=${body?.actorIds}&` : ""
    }organizationCode=${body?.organizationCode}&page=${body?.page}&size=${
      body?.size
    }&sort=${body?.sort}`,
    "GET",
    defaultProvide
  );
  return fetchRequest(descriptor);
};
